import { useCallback, useMemo } from 'react';

import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { useSelector } from 'react-redux';

import { useComparisonMode } from './useComparisonMode';
import type { OverviewResponseTimesData } from './useOverviewResponseTimes';
import type { OverviewSessionMetricsData } from './useOverviewSessionMetrics';
import { transformTimeseriesToChartData, type TimeseriesRow } from './useOverviewTimeseries';
import { useOverviewVariables } from './useOverviewVariables';
import type { MetricKey } from '../components/AnalyticsOverview/overviewChartConfig';
import { selectFilters } from '../redux/selectors';
import { getComparisonDates } from '../utils/getComparisonDates';

// ---------------------------------------------------------------------------
// GraphQL (separate operation names to avoid Apollo cache collisions)
// ---------------------------------------------------------------------------

const GET_COMPARISON_CONVERSATIONS_SUMMARY = gql`
  query AccountConversationsSummaryComparison(
    $startDate: timestamp
    $endDate: timestamp
    $brainIds: _uuid
    $channels: _text
    $deskIds: _uuid
    $isTest: Boolean
    $minNumUserMessages: Int
    $minAvgCsatScore: Float
    $maxAvgCsatScore: Float
  ) {
    rows: account_conversations_summary(
      args: {
        start_time: $startDate
        end_time: $endDate
        brain_parent_ids: $brainIds
        channels: $channels
        desk_ids: $deskIds
        is_test: $isTest
        min_num_user_messages: $minNumUserMessages
        min_avg_csat_score: $minAvgCsatScore
        max_avg_csat_score: $maxAvgCsatScore
      }
    ) {
      avg_ai_csat
      avg_ai_csat_insights
      ai_csat_insights_count
      avg_csat_user_rating
      csat_user_rating_count
      containment_rate
      total_conversations
      meaningful_conversations
      median_ai_session_length_seconds
      median_human_session_length_seconds
    }
  }
`;

const GET_COMPARISON_RESPONSE_TIMES_SUMMARY = gql`
  query AccountResponseTimesSummaryComparison(
    $startDate: timestamp
    $endDate: timestamp
    $brainIds: _uuid
    $channels: _text
    $deskIds: _uuid
    $isTest: Boolean
    $minNumUserMessages: Int
    $minAvgCsatScore: Float
    $maxAvgCsatScore: Float
  ) {
    rows: account_response_times_summary(
      args: {
        start_time: $startDate
        end_time: $endDate
        brain_parent_ids: $brainIds
        channels: $channels
        desk_ids: $deskIds
        is_test: $isTest
        min_num_user_messages: $minNumUserMessages
        min_avg_csat_score: $minAvgCsatScore
        max_avg_csat_score: $maxAvgCsatScore
      }
    ) {
      avg_response_time
      avg_ai_response_time
      ai_response_time_count
      avg_human_response_time
      human_response_time_count
    }
  }
`;

const GET_COMPARISON_CONVERSATIONS_TIMESERIES = gql`
  query AccountConversationsTimeseriesComparison(
    $startDate: timestamp
    $endDate: timestamp
    $brainIds: _uuid
    $channels: _text
    $deskIds: _uuid
    $isTest: Boolean
    $minNumUserMessages: Int
    $minAvgCsatScore: Float
    $maxAvgCsatScore: Float
  ) {
    rows: account_conversations_timeseries(
      args: {
        start_time: $startDate
        end_time: $endDate
        brain_parent_ids: $brainIds
        channels: $channels
        desk_ids: $deskIds
        is_test: $isTest
        min_num_user_messages: $minNumUserMessages
        min_avg_csat_score: $minAvgCsatScore
        max_avg_csat_score: $maxAvgCsatScore
      }
    ) {
      time_bucket
      avg_csat_score
      avg_ai_csat_insights
      ai_csat_insights_count
      avg_csat_user_rating
      csat_user_rating_count
      containment_rate
      total_conversations
    }
  }
`;

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useComparisonOverviewData = (selectedMetric: MetricKey, skipTimeseries = false) => {
  const { isComparing } = useComparisonMode();
  const filters = useSelector(selectFilters);

  const comparisonDates = useMemo(
    () => getComparisonDates(filters.startDate, filters.endDate),
    [filters.startDate, filters.endDate]
  );

  const { variables: comparisonVariables, skip: baseSkip } = useOverviewVariables(comparisonDates);
  const skip = baseSkip || !isComparing;

  // Session metrics query
  const {
    loading: metricsLoading,
    data: metricsData,
    error: metricsError,
    refetch: refetchMetrics,
  } = useQuery<{ rows: OverviewSessionMetricsData[] }>(GET_COMPARISON_CONVERSATIONS_SUMMARY, {
    variables: comparisonVariables,
    skip,
  });

  // Response times query
  const {
    loading: responseTimesLoading,
    data: responseTimesData,
    error: responseTimesError,
    refetch: refetchResponseTimes,
  } = useQuery<{ rows: OverviewResponseTimesData[] }>(GET_COMPARISON_RESPONSE_TIMES_SUMMARY, {
    variables: comparisonVariables,
    skip,
  });

  // Timeseries query
  const {
    loading: timeseriesLoading,
    data: timeseriesData,
    error: timeseriesError,
    refetch: refetchTimeseries,
  } = useQuery<{ rows: TimeseriesRow[] }>(GET_COMPARISON_CONVERSATIONS_TIMESERIES, {
    variables: comparisonVariables,
    skip: skip || skipTimeseries,
  });

  const chartData = useMemo(
    () => transformTimeseriesToChartData(timeseriesData?.rows, selectedMetric),
    [timeseriesData?.rows, selectedMetric]
  );

  const onRefetchMetrics = useCallback(() => {
    refetchMetrics(comparisonVariables);
  }, [refetchMetrics, comparisonVariables]);

  const onRefetchResponseTimes = useCallback(() => {
    refetchResponseTimes(comparisonVariables);
  }, [refetchResponseTimes, comparisonVariables]);

  const onRefetchTimeseries = useCallback(() => {
    refetchTimeseries(comparisonVariables);
  }, [refetchTimeseries, comparisonVariables]);

  const sessionMetricsResult = useMemo(
    () => ({
      isLoading: metricsLoading,
      error: metricsError,
      data: metricsData?.rows?.[0] ?? null,
      onRefetch: onRefetchMetrics,
    }),
    [metricsLoading, metricsError, metricsData, onRefetchMetrics]
  );

  const responseTimesResult = useMemo(
    () => ({
      isLoading: responseTimesLoading,
      error: responseTimesError,
      data: responseTimesData?.rows?.[0] ?? null,
      onRefetch: onRefetchResponseTimes,
    }),
    [responseTimesLoading, responseTimesError, responseTimesData, onRefetchResponseTimes]
  );

  const timeseriesResult = useMemo(
    () => ({
      isLoading: timeseriesLoading,
      error: timeseriesError,
      data: chartData,
      onRefetch: onRefetchTimeseries,
    }),
    [timeseriesLoading, timeseriesError, chartData, onRefetchTimeseries]
  );

  return {
    isComparing,
    sessionMetrics: sessionMetricsResult,
    responseTimes: responseTimesResult,
    timeseries: timeseriesResult,
  };
};
